import {
  AXES,
  LIBELLES_AXES,
  RUBRIQUES_TRAME,
  bornerHorodatage,
  evaluationSchema,
  majContactSchema,
  motifRefusCloture,
  resoudreEvaluation,
  type Axe,
  type BaremeApplicable,
  type ChampsNotation,
  type MajContact,
} from '@rh/shared';
import { depuisChampLocal } from '../format';
import type { BaremeAffiche, SaisieAppelLocale, SaisieEntretienLocale } from './types';

/**
 * Contrôles faits sur le téléphone avant toute mise en file, en ligne comme
 * hors ligne : ce qui est refusé ici n'entre pas dans la file. Mêmes règles que
 * l'API (schémas zod, `motifRefusCloture`, `resoudreEvaluation`,
 * `bornerHorodatage` de packages/shared), qui les vérifie de nouveau.
 */

export type IssueValidation<T> = { ok: true; valeur: T } | { ok: false; message: string };

export const MESSAGE_LIEU_HORS_LISTE = 'Lieu de rendez-vous : à choisir dans la liste.';
export const MESSAGE_NOTE_FORCEE = 'Indiquer la note forcée, entre 0 et 5.';
export const MESSAGE_BAREME_NON_CHARGE =
  'Barème non chargé (hors ligne) : la notation s’affichera au retour du réseau. La trame reste modifiable.';
const MESSAGE_DATE_ILLISIBLE = 'Date illisible : ressaisissez le rendez-vous ou le rappel.';
const MESSAGE_HEURE_ILLISIBLE = 'Heure de saisie illisible.';

/** Corps du PUT d'un appel : un candidat non joint n'a ni disponibilité, ni pastille, ni rendez-vous. */
export function corpsAppel(saisie: SaisieAppelLocale): MajContact {
  const joint = saisie.joignable === true;
  return {
    joignable: saisie.joignable,
    disponibleDeSuite: joint ? saisie.disponible : null,
    interesse: joint ? saisie.interesse : null,
    potentiel: joint ? saisie.potentiel : null,
    rdvLe: joint ? depuisChampLocal(saisie.rdv) : null,
    rdvLieuId: joint && saisie.rdv && saisie.lieu ? saisie.lieu : null,
    rappelLe: depuisChampLocal(saisie.rappel),
    observations: saisie.observations.trim() || null,
  };
}

export function corpsTrame(saisie: SaisieEntretienLocale): MajContact {
  return { reponses: saisie.reponses, observations: saisie.observations.trim() || null };
}

/** Rien de la notation n'a été touché (le PUT de la trame ne l'emporte pas). */
export function notationVide(saisie: SaisieEntretienLocale): boolean {
  return (
    AXES.every((axe) => saisie.scores[axe] === undefined) &&
    !saisie.eliminatoire &&
    saisie.motif === '' &&
    saisie.precision.trim() === '' &&
    !saisie.forcer &&
    saisie.noteForcee.trim() === '' &&
    saisie.justification.trim() === ''
  );
}

const LIBELLES_CHAMPS: Record<string, string> = {
  joignable: 'Candidat joint',
  disponibleDeSuite: 'Disponible de suite',
  interesse: 'Intéressé par le poste',
  observations: 'Observations',
  reponses: 'Trame',
  scores: 'Notation',
  eliminatoire: 'Critère éliminatoire',
  precisionEliminatoire: 'Précision',
  justification: 'Justification',
};

/** Seule erreur possible sur ces textes : la longueur (le formulaire en fixe le type). */
const LONGUEURS_MAX: Record<string, string> = {
  observations: '4 000',
  precisionEliminatoire: '1 000',
  justification: '2 000',
};

function messageIssue(path: ReadonlyArray<string | number>, message: string): string {
  const [racine, sous] = path;
  if (racine === 'reponses' && typeof sous === 'string') {
    const rubrique = RUBRIQUES_TRAME.find((r) => r.cle === sous);
    if (rubrique) return `${rubrique.libelle} : 4 000 caractères au plus.`;
  }
  if (racine === 'scores' && typeof sous === 'string' && sous in LIBELLES_AXES) {
    return `${LIBELLES_AXES[sous as Axe]} : score entre 0 et 5.`;
  }
  if (typeof racine === 'string' && racine in LONGUEURS_MAX) {
    return `${LIBELLES_CHAMPS[racine]} : ${LONGUEURS_MAX[racine]} caractères au plus.`;
  }
  switch (racine) {
    case 'rdvLe':
      return 'Rendez-vous : date illisible.';
    case 'rappelLe':
      return 'Rappel : date illisible.';
    case 'rdvLieuId':
      return MESSAGE_LIEU_HORS_LISTE;
    case 'potentiel':
      return 'Pastille de potentiel : entre 0 et 5.';
    case 'noteFinale':
      return MESSAGE_NOTE_FORCEE;
    case 'motifEliminatoire':
      return 'Motif éliminatoire : à choisir dans la liste.';
  }
  const libelle = typeof racine === 'string' ? LIBELLES_CHAMPS[racine] : undefined;
  return libelle ? `${libelle} : ${message}` : message;
}

/** Erreurs zod traduites par champ, sans doublon. */
export function messageZod(issues: ReadonlyArray<{ path: ReadonlyArray<string | number>; message: string }>): string {
  return [...new Set(issues.map((issue) => messageIssue(issue.path, issue.message)))].join(' ');
}

/**
 * `lieuxConnus` : identifiants des lieux chargés, plus celui déjà enregistré sur
 * le contact (un lieu désactivé depuis reste accepté tant qu'il ne change pas).
 * null quand la liste n'a pas pu être chargée : l'API contrôlera.
 */
export function validerEnregistrementAppel(
  saisie: SaisieAppelLocale,
  lieuxConnus: readonly string[] | null,
): IssueValidation<MajContact> {
  let corps: MajContact;
  try {
    corps = corpsAppel(saisie);
  } catch {
    return { ok: false, message: MESSAGE_DATE_ILLISIBLE };
  }
  const lu = majContactSchema.safeParse(corps);
  if (!lu.success) return { ok: false, message: messageZod(lu.error.issues) };
  if (corps.rdvLieuId && lieuxConnus !== null && !lieuxConnus.includes(corps.rdvLieuId)) {
    return { ok: false, message: MESSAGE_LIEU_HORS_LISTE };
  }
  return { ok: true, valeur: corps };
}

/** L'API relit le contact après le PUT complet : il vaut alors exactement ce corps. */
export function validerClotureAppel(
  saisie: SaisieAppelLocale,
  lieuxConnus: readonly string[] | null,
): IssueValidation<MajContact> {
  const issue = validerEnregistrementAppel(saisie, lieuxConnus);
  if (!issue.ok) return issue;
  const motif = motifRefusCloture({
    type: 'telephonique',
    joignable: issue.valeur.joignable ?? null,
    potentiel: issue.valeur.potentiel ?? null,
    rdvLe: issue.valeur.rdvLe ?? null,
  });
  return motif ? { ok: false, message: motif } : issue;
}

export function validerTrame(saisie: SaisieEntretienLocale): IssueValidation<MajContact> {
  const corps = corpsTrame(saisie);
  const lu = majContactSchema.safeParse(corps);
  return lu.success ? { ok: true, valeur: corps } : { ok: false, message: messageZod(lu.error.issues) };
}

export function validerEvaluation(
  saisie: SaisieEntretienLocale,
  bareme: BaremeApplicable | null,
): IssueValidation<{ corps: MajContact; entree: ChampsNotation; bareme: BaremeAffiche; noteAffichee: number }> {
  if (!bareme) return { ok: false, message: MESSAGE_BAREME_NON_CHARGE };
  const trame = validerTrame(saisie);
  if (!trame.ok) return trame;

  const forcage = !saisie.eliminatoire && saisie.forcer;
  const note = Number(saisie.noteForcee.replace(',', '.'));
  if (forcage && (saisie.noteForcee.trim() === '' || !Number.isFinite(note) || note < 0 || note > 5)) {
    return { ok: false, message: MESSAGE_NOTE_FORCEE };
  }
  const entree: ChampsNotation = {
    scores: saisie.scores,
    eliminatoire: saisie.eliminatoire,
    motifEliminatoire: saisie.eliminatoire && saisie.motif ? saisie.motif : null,
    precisionEliminatoire: saisie.eliminatoire ? saisie.precision.trim() || null : null,
    noteFinale: forcage ? note : null,
    justification: forcage ? saisie.justification.trim() || null : null,
  };
  const lu = evaluationSchema.safeParse(entree);
  if (!lu.success) return { ok: false, message: messageZod(lu.error.issues) };
  const issue = resoudreEvaluation(entree, bareme.poids);
  if (!issue.valide) return { ok: false, message: issue.erreur };
  return {
    ok: true,
    valeur: {
      corps: trame.valeur,
      entree,
      bareme: { id: bareme.id, version: bareme.version },
      noteAffichee: issue.noteFinale,
    },
  };
}

/**
 * Heure de clôture ou d'évaluation contrôlée avec les bornes de l'API, sur
 * l'heure du serveur estimée. L'heure rendue est celle fournie : l'API la
 * ramènera elle-même entre l'ouverture et son heure.
 */
export function validerHeure(heure: string, debuteLe: string, maintenant: number): IssueValidation<string> {
  const t = Date.parse(heure);
  const debut = Date.parse(debuteLe);
  if (Number.isNaN(t) || Number.isNaN(debut) || !Number.isFinite(maintenant)) {
    return { ok: false, message: MESSAGE_HEURE_ILLISIBLE };
  }
  const issue = bornerHorodatage(new Date(t), { debuteLe: new Date(debut), maintenant: new Date(maintenant) });
  return issue.ok ? { ok: true, valeur: new Date(t).toISOString() } : { ok: false, message: issue.message };
}
